import type { LiubaLocalLessonVenueId } from "@/lib/localLessons/liubaVenues";

export const LOCAL_LESSON_PRICING_ROLLOUT_VERSION = "uk-local-pricing-2026-07-14-packages-v2" as const;
export const FLEXIBLE_PAYMENT_DISCLAIMER =
  "Flexible payment options may be available at secure checkout, subject to eligibility." as const;
export const CHECKOUT_FLEXIBLE_PAYMENT_DISCLAIMER =
  "Flexible payment options may be available at secure checkout, subject to eligibility." as const;

const BOTH_STUDIOS = ["ely_studio", "bury_st_edmunds_studio"] as const;

export const LOCAL_LESSON_PRODUCT_CATALOG = {
  liuba_intro_lesson: {
    productKey: "liuba_intro_lesson",
    displayName: "Voice Discovery Lesson",
    shortName: "Voice Discovery Lesson",
    sessionCount: 1,
    sessionDurationMinutes: 30,
    totalCoachingMinutes: 30,
    amountMinor: 7_500,
    amountPounds: 75,
    currency: "gbp",
    eligibleVenues: BOTH_STUDIOS,
    stripeProductName: "Voice Discovery Lesson",
    stripePriceEnvKey: "STRIPE_LOCAL_INTRO_LESSON_PRICE_ID",
    active: true,
    public: true,
    historical: false,
    publicGroup: "voice_discovery",
    publicOrder: 0,
    promoteFlexiblePayments: false,
    format: "voice_discovery",
    publicDescription: "A private 30-minute Voice Discovery Lesson with Liuba Doga, including an emailed voice report.",
  },
  liuba_single_lesson: {
    productKey: "liuba_single_lesson",
    displayName: "1 × 60-Minute In-Person Coaching",
    shortName: "1-Hour Coaching Session",
    sessionCount: 1,
    sessionDurationMinutes: 60,
    totalCoachingMinutes: 60,
    amountMinor: 9_500,
    amountPounds: 95,
    currency: "gbp",
    eligibleVenues: BOTH_STUDIOS,
    stripeProductName: "1 Hour In-Person Coaching",
    stripePriceEnvKey: "STRIPE_LOCAL_SINGLE_LESSON_PRICE_ID",
    active: true,
    public: true,
    historical: false,
    publicGroup: "full_hour",
    publicOrder: 1,
    promoteFlexiblePayments: false,
    format: "full_hour",
    publicDescription: "One private one-hour in-person coaching session with Liuba Doga.",
  },
  liuba_lesson_pack_3: {
    productKey: "liuba_lesson_pack_3",
    displayName: "3 × 60-Minute In-Person Coaching",
    shortName: "3-Hour Coaching Package",
    sessionCount: 3,
    sessionDurationMinutes: 60,
    totalCoachingMinutes: 180,
    amountMinor: 27_000,
    amountPounds: 270,
    currency: "gbp",
    eligibleVenues: BOTH_STUDIOS,
    stripeProductName: "3 × 60-Minute In-Person Coaching",
    stripePriceEnvKey: "STRIPE_LOCAL_LESSON_PACK_3_PRICE_ID",
    active: true,
    public: true,
    historical: false,
    publicGroup: "full_hour",
    publicOrder: 2,
    promoteFlexiblePayments: true,
    format: "full_hour",
    publicDescription: "Three separate one-hour in-person coaching sessions with flexible booking.",
    historicalPriceAmountsMinor: [27_500],
  },
  liuba_lesson_pack_4: {
    productKey: "liuba_lesson_pack_4",
    displayName: "4-Hour In-Person Coaching Package",
    shortName: "4-Hour Coaching Package",
    sessionCount: 4,
    sessionDurationMinutes: 60,
    totalCoachingMinutes: 240,
    amountMinor: 36_000,
    amountPounds: 360,
    currency: "gbp",
    eligibleVenues: BOTH_STUDIOS,
    stripeProductName: "4 Hour In-Person Coaching Package",
    stripePriceEnvKey: "STRIPE_LOCAL_LESSON_PACK_4_PRICE_ID",
    active: false,
    public: false,
    historical: true,
    publicGroup: "historical",
    publicOrder: 0,
    promoteFlexiblePayments: true,
    format: "full_hour",
    publicDescription: "Four separate one-hour in-person coaching sessions with Liuba Doga.",
    historicalPriceAmountsMinor: [37_500],
  },
  liuba_lesson_pack_6: {
    productKey: "liuba_lesson_pack_6",
    displayName: "6 × 60-Minute In-Person Coaching",
    shortName: "6-Hour Coaching Package",
    sessionCount: 6,
    sessionDurationMinutes: 60,
    totalCoachingMinutes: 360,
    amountMinor: 51_500,
    amountPounds: 515,
    currency: "gbp",
    eligibleVenues: BOTH_STUDIOS,
    stripeProductName: "6-Hour In-Person Coaching Package",
    stripePriceEnvKey: "STRIPE_LOCAL_LESSON_PACK_6_PRICE_ID",
    active: true,
    public: true,
    historical: false,
    publicGroup: "full_hour",
    publicOrder: 3,
    promoteFlexiblePayments: true,
    format: "full_hour",
    publicDescription: "Six separate one-hour in-person coaching sessions with Liuba Doga.",
  },
  liuba_lesson_pack_10: {
    productKey: "liuba_lesson_pack_10",
    displayName: "10 × 60-Minute In-Person Coaching",
    shortName: "10-Hour Coaching Package",
    sessionCount: 10,
    sessionDurationMinutes: 60,
    totalCoachingMinutes: 600,
    amountMinor: 82_500,
    amountPounds: 825,
    currency: "gbp",
    eligibleVenues: BOTH_STUDIOS,
    stripeProductName: "10 × 60-Minute In-Person Coaching",
    stripePriceEnvKey: "STRIPE_LOCAL_LESSON_PACK_10_PRICE_ID",
    active: true,
    public: true,
    historical: false,
    publicGroup: "full_hour",
    publicOrder: 4,
    promoteFlexiblePayments: true,
    format: "full_hour",
    publicDescription: "Ten separate one-hour in-person coaching sessions with Liuba Doga.",
  },
  liuba_half_hour_pack_6: {
    productKey: "liuba_half_hour_pack_6",
    displayName: "6 × 30-Minute In-Person Sessions",
    shortName: "6 Short Coaching Sessions",
    sessionCount: 6,
    sessionDurationMinutes: 30,
    totalCoachingMinutes: 180,
    amountMinor: 27_500,
    amountPounds: 275,
    currency: "gbp",
    eligibleVenues: ["ely_studio"] as const,
    stripeProductName: "6 × 30-Minute In-Person Sessions",
    stripePriceEnvKey: "STRIPE_LOCAL_HALF_HOUR_PACK_6_PRICE_ID",
    active: true,
    public: true,
    historical: false,
    publicGroup: "short_session",
    publicOrder: 5,
    promoteFlexiblePayments: false,
    format: "half_hour",
    publicDescription: "Six separate 30-minute in-person sessions at Ely Studio.",
  },
} as const;

export type LocalLessonProductKey = keyof typeof LOCAL_LESSON_PRODUCT_CATALOG;
export type LocalLessonProduct = (typeof LOCAL_LESSON_PRODUCT_CATALOG)[LocalLessonProductKey];

export const LOCAL_LESSON_PRODUCT_KEYS = Object.keys(LOCAL_LESSON_PRODUCT_CATALOG) as LocalLessonProductKey[];

export function isLocalLessonProductKey(value: string): value is LocalLessonProductKey {
  return Object.prototype.hasOwnProperty.call(LOCAL_LESSON_PRODUCT_CATALOG, value);
}

export function getLocalLessonProduct(productKey: LocalLessonProductKey) {
  return LOCAL_LESSON_PRODUCT_CATALOG[productKey];
}

export function isLocalLessonProductAvailableAtVenue(
  productKey: LocalLessonProductKey,
  venueId: LiubaLocalLessonVenueId,
) {
  return (getLocalLessonProduct(productKey).eligibleVenues as readonly string[]).includes(venueId);
}

export function getPublicLocalLessonProducts(venueId?: LiubaLocalLessonVenueId) {
  return LOCAL_LESSON_PRODUCT_KEYS
    .map(getLocalLessonProduct)
    .filter((product) => product.active && product.public)
    .filter((product) => !venueId || (product.eligibleVenues as readonly string[]).includes(venueId))
    .sort((a, b) => a.publicOrder - b.publicOrder);
}

export function formatLocalLessonPrice(amountMinor: number) {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: amountMinor % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amountMinor / 100);
}

export function formatSessionDuration(minutes: number) {
  if (minutes === 60) return "1 hour";
  return `${minutes} minutes`;
}

export function formatTotalCoachingTime(minutes: number) {
  if (minutes % 60 === 0) {
    const hours = minutes / 60;
    return `${hours} ${hours === 1 ? "hour" : "hours"}`;
  }
  return `${minutes} minutes`;
}

export function getLocalLessonSavingsMinor(productKey: LocalLessonProductKey) {
  const product = getLocalLessonProduct(productKey);
  if (product.format !== "full_hour" || product.sessionCount <= 1) return 0;
  const single = getLocalLessonProduct("liuba_single_lesson");
  return Math.max(0, single.amountMinor * product.sessionCount - product.amountMinor);
}

export function getLocalLessonPriceDetail(productKey: LocalLessonProductKey) {
  const product = getLocalLessonProduct(productKey);
  if (product.productKey === "liuba_intro_lesson") return "30-minute private lesson plus emailed report";
  if (product.sessionCount === 1) return `${formatLocalLessonPrice(product.amountMinor)} per hour`;
  const savings = getLocalLessonSavingsMinor(productKey);
  const format = `${product.sessionCount} × ${formatSessionDuration(product.sessionDurationMinutes)} sessions`;
  return savings > 0 ? `${format} · saves ${formatLocalLessonPrice(savings)}` : format;
}

export function getLocalLessonAnalyticsMetadata(productKey: LocalLessonProductKey) {
  const product = getLocalLessonProduct(productKey);
  return {
    product_key: product.productKey,
    session_count: product.sessionCount,
    session_duration_minutes: product.sessionDurationMinutes,
    total_coaching_minutes: product.totalCoachingMinutes,
    total_amount_minor: product.amountMinor,
  };
}
